import { MoreVertical, ShieldOff, UserCog } from 'lucide-react';
import { useRef, useState } from 'react';

import { Avatar } from './Avatar';
import { Badge } from './Badge';
import { formatRelativeTime } from '../lib/format-relative-time';

import type { UserRole } from '../lib/types';

export interface UserListItemProps {
  id: string;
  email: string;
  displayName: string | null;
  role: UserRole;
  createdAt: string;
  isDeactivated?: boolean | undefined;
  actorRole?: UserRole | undefined;
  onChangeRole?: ((newRole: UserRole) => void) | undefined;
  onDeactivate?: (() => void) | undefined;
  className?: string | undefined;
}

const ROLE_BADGE: Record<UserRole, { variant: 'warning' | 'info' | 'success'; label: string }> = {
  owner: { variant: 'warning', label: 'Owner' },
  admin: { variant: 'info', label: 'Admin' },
  member: { variant: 'success', label: 'Member' },
};

const ROLE_RANK: Record<UserRole, number> = { owner: 3, admin: 2, member: 1 };

export function UserListItem({
  email,
  displayName,
  role,
  createdAt,
  isDeactivated,
  actorRole,
  onChangeRole,
  onDeactivate,
  className = '',
}: UserListItemProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const badge = ROLE_BADGE[role];
  const name = displayName ?? email.split('@')[0] ?? email;

  const canManage = actorRole && ROLE_RANK[actorRole] > ROLE_RANK[role];
  const hasActions = canManage && (onChangeRole || onDeactivate) && !isDeactivated;

  return (
    <div
      role="listitem"
      className={`flex items-center gap-sd-2 min-h-[56px] px-sd-3 py-sd-2 bg-sd-surface border-b border-sd-border/20 transition-all duration-150 ease-in motion-reduce:transition-none ${isDeactivated ? 'opacity-60' : ''} ${className}`.trim()}
    >
      <Avatar name={name} size="sm" />

      <div className="flex flex-col min-w-0 flex-1">
        <span className={`text-sd-base font-medium text-sd-text truncate ${isDeactivated ? 'line-through' : ''}`}>
          {name}
        </span>
        <span className="text-sd-sm text-sd-text-secondary truncate">{email}</span>
      </div>

      <Badge variant={badge.variant} size="sm">{badge.label}</Badge>

      <span className="text-sd-xs text-sd-text-secondary shrink-0 hidden @md:inline">
        {formatRelativeTime(createdAt)}
      </span>

      {hasActions && (
        <div className="relative shrink-0" ref={menuRef}>
          <button
            aria-label="User actions"
            className="p-sd-1 rounded-sd-sm text-sd-text-secondary hover:bg-sd-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary transition-colors duration-150"
            onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
          >
            <MoreVertical size={16} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-sd-1 w-44 bg-sd-surface-elevated border border-sd-border/30 rounded-sd-md shadow-sd-md z-30 py-sd-1">
              {onChangeRole && role !== 'admin' && actorRole === 'owner' && (
                <button
                  className="flex items-center gap-sd-2 w-full px-sd-3 py-sd-2 text-sd-sm text-sd-text hover:bg-sd-surface transition-colors"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onChangeRole('admin'); }}
                >
                  <UserCog size={14} /> Promote to Admin
                </button>
              )}
              {onChangeRole && role === 'admin' && actorRole === 'owner' && (
                <button
                  className="flex items-center gap-sd-2 w-full px-sd-3 py-sd-2 text-sd-sm text-sd-text hover:bg-sd-surface transition-colors"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onChangeRole('member'); }}
                >
                  <UserCog size={14} /> Demote to Member
                </button>
              )}
              {onDeactivate && (
                <button
                  className="flex items-center gap-sd-2 w-full px-sd-3 py-sd-2 text-sd-sm text-sd-error hover:bg-sd-surface transition-colors"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDeactivate(); }}
                >
                  <ShieldOff size={14} /> Deactivate
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
